'use client';

import { useTranslations } from 'next-intl';
import { bandStatus, platformName } from '@/lib/scanners/labels';
import type { ChannelResult, DimensionScore, SiteHealth } from '@/lib/scanners/types';
import { SeverityMark } from '@/components/audit/SeverityMark';
import styles from './SiteHealthBlock.module.css';

/**
 * The site-health layer of a paid report: the scorecard (five channels, one
 * composite) and the three rule-based channels with their findings and what
 * is already right. Copy lives in `report.health`, shared with the PDF.
 * Design system: mono labels, horizontal borders, one meter per row, no cards.
 */

interface Props {
  health: SiteHealth;
  locale?: string;
}

const CHANNELS = ['seo', 'geo', 'performance'] as const;

export function SiteHealthBlock({ health, locale = 'en' }: Props) {
  const t = useTranslations('report.health');
  const lang: 'en' | 'es' = locale === 'es' ? 'es' : 'en';
  const nf = new Intl.NumberFormat(lang === 'es' ? 'es-ES' : 'en-US');
  const sc = health.scorecard;
  const platform = platformName(health.profile.platform);

  return (
    <section className={styles.block} aria-labelledby="site-health-heading">
      <span className={styles.label}>{t('chapterLabel')}</span>
      <div className={styles.head}>
        <div>
          <h2 id="site-health-heading" className={styles.headline}>
            {t('chapterTitle')}
          </h2>
          <p className={styles.lead}>{t('lead')}</p>
        </div>
        <div className={styles.composite} data-status={bandStatus(sc.band)}>
          <span className={styles.compositeLabel}>{t('composite')}</span>
          <span className={styles.compositeValue}>{nf.format(sc.composite)}</span>
          <span className={styles.compositeBand}>{t(`bands.${sc.band}`)}</span>
        </div>
      </div>
      <p className={styles.note}>
        {t('compositeNote', { siteType: t(`siteTypes.${sc.siteType}`) })} {t('platform')}: {platform ?? t('platformUnknown')}.
      </p>

      <ol className={styles.rows} aria-label={t('channelsTitle')}>
        {sc.dimensions.map((d) => (
          <DimensionRow key={d.dimension} d={d} t={t} nf={nf} />
        ))}
      </ol>

      {CHANNELS.map((key) => (
        <Channel key={key} id={key} result={health[key]} health={health} t={t} nf={nf} lang={lang} />
      ))}
    </section>
  );
}

type T = ReturnType<typeof useTranslations<'report.health'>>;

function DimensionRow({ d, t, nf }: { d: DimensionScore; t: T; nf: Intl.NumberFormat }) {
  const status = bandStatus(d.band);
  return (
    <li className={styles.row} data-status={d.present ? status : 'none'}>
      <span className={styles.rowName}>{t(`dimensions.${d.dimension}`)}</span>
      {d.present ? (
        <>
          <span className={styles.rowScore}>{nf.format(d.score)}</span>
          <span className={styles.rowBand}>{t(`bands.${d.band}`)}</span>
          <span className={styles.meter} aria-hidden="true">
            <span className={styles.meterFill} style={{ width: `${d.score}%` }} />
          </span>
        </>
      ) : (
        <span className={styles.rowNotAssessed}>
          {t('notAssessed')}
          {d.notAssessed ? `: ${t(`reasons.${d.notAssessed}`)}` : ''}
        </span>
      )}
    </li>
  );
}

function Channel({
  id,
  result,
  health,
  t,
  nf,
  lang,
}: {
  id: (typeof CHANNELS)[number];
  result: ChannelResult<unknown>;
  health: SiteHealth;
  t: T;
  nf: Intl.NumberFormat;
  lang: 'en' | 'es';
}) {
  const dim = health.scorecard.dimensions.find((d) => d.dimension === id);
  const cwv = id === 'performance' ? health.performance.data.cwv : null;
  return (
    <div className={styles.channel}>
      <div className={styles.channelHead}>
        <h3 className={styles.channelTitle}>{t(`dimensions.${id}`)}</h3>
        <span className={styles.channelScore} data-status={dim?.present ? bandStatus(dim.band) : 'none'}>
          {dim?.present ? nf.format(dim.score) : t('notAssessed')}
        </span>
      </div>
      <p className={styles.channelLead}>{t(`channelLeads.${id}`)}</p>
      {result.notAssessed ? (
        <p className={styles.channelNote}>
          {t('notAssessed')}: {t(`reasons.${result.notAssessed}`)}.
        </p>
      ) : null}

      {cwv ? (
        <dl className={styles.vitals}>
          {cwv.measured ? (
            <>
              <Vital label={t('vitals.lcp')} value={cwv.lcpMs !== undefined ? `${nf.format(cwv.lcpMs)} ms` : ''} />
              <Vital label={t('vitals.cls')} value={cwv.cls !== undefined ? new Intl.NumberFormat(lang === 'es' ? 'es-ES' : 'en-US', { maximumFractionDigits: 3 }).format(cwv.cls) : ''} />
              <Vital label={t('vitals.ttfb')} value={cwv.ttfbMs !== undefined ? `${nf.format(cwv.ttfbMs)} ms` : ''} />
            </>
          ) : null}
          <div className={styles.vitalNote}>{cwv.measured ? t('vitals.labNote') : t('vitals.notMeasured')}</div>
        </dl>
      ) : null}

      {result.strengths.length ? (
        <p className={styles.strengths}>
          <span className={styles.strengthsLabel}>{t('strengths')}</span>
          {result.strengths.map((s) => t(`strengthLabels.${s}`)).join(' · ')}
        </p>
      ) : null}

      {result.findings.length ? (
        <ol className={styles.findings}>
          {result.findings.map((f) => (
            <li key={f.id} className={styles.finding}>
              <div className={styles.findingHead}>
                <h4 className={styles.findingTitle}>{f.title}</h4>
                <SeverityMark severity={f.severity} />
              </div>
              <p className={styles.findingBody}>{f.description}</p>
              {f.recommendation ? <p className={styles.findingFix}>{f.recommendation}</p> : null}
            </li>
          ))}
        </ol>
      ) : !result.notAssessed ? (
        <p className={styles.channelNote}>{t('noFindings')}</p>
      ) : null}
    </div>
  );
}

function Vital({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.vital}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
